import { BrowserOptions, init as SentryInit } from '@sentry/browser';
import React from 'react';
import { Environment } from './Environment';

const CHROME_EXTENSION = /(chrome-extension)+(.)*/;
const SAFARI_EXTENSION = /(safari-extension)+(.)*/;
const HOTJAR = /(https:\/\/static\.hotjar\.com)+(.)*/;
const BGALYTICS = /(https:\/\/analytics\.bgalytics\.com)+(.)*/;

export const beforeSend: BrowserOptions['beforeSend'] = error => {
  const { extra, transaction } = error;

  if (transaction && HOTJAR.test(transaction)) return null;

  if (extra && extra.body) {
    extra.handled = 'UKNOWN';

    try {
      const body = typeof extra.body === 'string' ? JSON.parse(extra.body) : extra.body;

      extra.handled = 'VALID_JSON';

      if (body) {
        const sourceFile = body.sourceFile;

        if (sourceFile &&
            [BGALYTICS, SAFARI_EXTENSION, CHROME_EXTENSION].some(matcher => matcher.test(sourceFile))) {
          return null;
        }
      }

      extra.handled = 'MISSING_BODY';
    } catch (e) {
      // ignore parse error
      extra.handled = 'INVALID_JSON';
    }
  }

  return error;
};

export function initSentry() {
  if (Environment.current.isCloverHost) {
    SentryInit({
      dsn: 'https://529197cb04a243d18f7d925e609a29cd@sentry.io/1296832',
      environment: Environment.current.environmentFriendlyName,
      release: COMMIT_HASH,
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Might be an adblocking browser as this only happens for ad links
        'HTMLDocument.DOMNodeInsertedFunc',

        // _elqQ error
        '_elqQ',
      ],
      beforeSend,
    });
  }
}

export function removeExpensiveNodes(payload: any) {
  if (!payload) return payload;

  const visited = new Set();

  const ret = {};

  const queue = [[ret, payload]];

  while (queue.length > 0) {
    const [ memo, current ] = queue.shift()!;

    if (visited.has(current)) continue;

    visited.add(current);

    Object.keys(current).forEach(key => {
      const value = current[key];

      if (React.isValidElement(value) && React.Children.count(value) > 0) {
        memo[key] = '[ReactNode]';
        return;
      }

      if (value instanceof Node) {
        memo[key] = '[Node]';
        return;
      }

      const valueType = typeof value;

      switch (valueType) {
        case 'function':
          memo[key] = '[Function]';
          return;
        case 'boolean':
        case 'bigint':
        case 'number':
        case 'string':
        case 'symbol':
        case 'undefined':
          memo[key] = value;
          return;
        case 'object':
          break;
      }

      if (value === null) {
        memo[key] = null;
        return;
      }

      if (visited.has(value)) {
        memo[key] = '[Circular]';
        return;
      }

      const newMemo = memo[key] = {};

      queue.push([ newMemo, value ]);
    });
  }

  return ret;
}
